import React, { useState, useEffect } from 'react';
import { Users, GraduationCap, CalendarCheck, DollarSign, TrendingUp, TrendingDown, AlertTriangle, Activity } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, Legend
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api';
import { PushNotificationsBanner } from '../../components/PushNotifications';

/* ── Sparkline ─────────────────────────────────────────────────── */
function Sparkline({ data, color = '#16A34A' }) {
  const vals = data || [3, 5, 4, 8, 6, 9, 7];
  return (
    <ResponsiveContainer width={80} height={40}>
      <AreaChart data={vals.map((v, i) => ({ i, v }))} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id={`spark-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.15} />
            <stop offset="95%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} fill={`url(#spark-${color.replace('#', '')})`} dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

/* ── Stat Card ─────────────────────────────────────────────────── */
function StatCard({ icon: Icon, label, value, sub, color, sparkColor, idx }) {
  return (
    <div className="stat-card animate-fade-in-up" style={{ animationDelay: `${idx * 0.07}s` }}>
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center icon-circle-${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <Sparkline color={sparkColor} />
      </div>
      <div className="mt-3">
        <p className="text-2xl font-bold text-slate-900 font-manrope">{value}</p>
        <p className="text-sm font-medium text-slate-500 mt-0.5">{label}</p>
        {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
      </div>
    </div>
  );
}

/* ── Progress Bar Row ──────────────────────────────────────────── */
function ProgressRow({ label, value, total, color }) {
  const pct = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;
  return (
    <div className="mb-3.5 last:mb-0">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm text-slate-700">{label}</span>
        <span className="text-sm font-semibold text-slate-900">{value.toLocaleString()} / {total.toLocaleString()}</span>
      </div>
      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

/* ── Main Dashboard ────────────────────────────────────────────── */
export default function SchoolDashboard() {
  const { user, school } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [feeData, setFeeData] = useState(null);
  const [classData, setClassData] = useState([]);
  const [healthScore, setHealthScore] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/school/dashboard'),
      api.get('/analytics/health-score'),
      api.get('/fees/summary'),
      api.get('/school/analytics'),
    ]).then(([dashRes, healthRes, feeRes, analyticsRes]) => {
      setData(dashRes.data?.data || dashRes.data);
      if (healthRes.success) setHealthScore(healthRes.data);
      const feePayload = feeRes.data?.data || feeRes.data;
      if (feePayload) setFeeData(feePayload);
      // Build class distribution from real analytics
      const analyticsPayload = analyticsRes.data?.data || analyticsRes.data;
      const byClass = analyticsPayload?.students_by_class || [];
      if (byClass.length > 0) {
        setClassData(byClass.map(c => ({ name: c._id || c.class_name || 'Other', count: c.count || 0 })));
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const s = data?.stats || {};
  const trend = data?.attendance_trend || [];
  const recentStudents = data?.recent_students || [];

  const firstName = user?.first_name || 'Admin';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const today = new Date().toLocaleDateString('en-NG', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  /* Attendance totals */
  const totalStudents = s.total_students || 0;
  const presentToday = s.today_present || 0;
  const absentToday = s.today_absent || 0;

  /* Real fee data */
  const feeTotal = feeData?.total_invoiced || 0;
  const feePaid = feeData?.total_collected || 0;
  const feeBalance = feeData?.outstanding || (feeTotal - feePaid);
  const feePendingCount = s.pending_invoices || 0;

  /* Format currency */
  const fmt = (n) => n >= 1e6 ? `₦${(n / 1e6).toFixed(1)}M` : n >= 1000 ? `₦${(n / 1000).toFixed(0)}K` : `₦${n}`;

  const statCards = [
    { icon: Users, label: 'Total Students', value: loading ? '—' : totalStudents.toLocaleString(), sub: `${s.male_students || 0} Male · ${s.female_students || 0} Female`, color: 'indigo', sparkColor: '#4F46E5', idx: 0 },
    { icon: GraduationCap, label: 'Total Teachers', value: loading ? '—' : (s.total_teachers || 0), sub: `${s.total_classes || 0} Classes · ${s.total_subjects || 0} Subjects`, color: 'teal', sparkColor: '#0D9488', idx: 1 },
    { icon: CalendarCheck, label: 'Present Today', value: loading ? '—' : presentToday.toLocaleString(), sub: `${totalStudents > 0 ? Math.round((presentToday / totalStudents) * 100) : 0}% attendance rate`, color: 'success', sparkColor: '#16A34A', idx: 2 },
    { icon: DollarSign, label: 'Outstanding Fees', value: loading ? '—' : fmt(feeBalance), sub: `${feePendingCount} unpaid invoices`, color: 'warning', sparkColor: '#EAB308', idx: 3 },
  ];

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fade-in" data-testid="school-dashboard">
      {/* ── Welcome Header ──────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-manrope text-2xl font-bold text-slate-900">{greeting}, {firstName}!</h1>
          <p className="text-slate-500 text-sm mt-0.5">{today}</p>
        </div>
        <div className="text-right">
          <p className="text-sm font-semibold text-slate-800">{school?.name}</p>
          <p className="text-xs text-slate-400">{school?.city}, {school?.state}</p>
        </div>
      </div>

      {/* ── Stat Cards ─────────────────────────────────────────── */}
      <PushNotificationsBanner />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map(card => <StatCard key={card.label} {...card} />)}
      </div>

      {/* ── School Health Score Widget ───────────────────────────── */}
      {(healthScore || loading) && (
        <div className="edu-card p-5" data-testid="health-score-widget">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-600" />
              <h3 className="font-manrope text-base font-semibold text-slate-900">School Health Score</h3>
            </div>
            <button
              onClick={() => navigate('/school/business-view')}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium"
              data-testid="view-business-btn"
            >
              Business View →
            </button>
          </div>
          {loading && !healthScore ? (
            <div className="flex gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-16 flex-1 bg-slate-100 rounded-lg animate-pulse" />)}</div>
          ) : healthScore ? (
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              {/* Overall score circle */}
              <div className="flex items-center gap-4 flex-shrink-0">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center border-4 ${
                  healthScore.overall_score >= 70 ? 'border-emerald-400 bg-emerald-50' :
                  healthScore.overall_score >= 50 ? 'border-yellow-400 bg-yellow-50' : 'border-red-400 bg-red-50'
                }`}>
                  <span className={`text-xl font-bold ${
                    healthScore.overall_score >= 70 ? 'text-emerald-700' :
                    healthScore.overall_score >= 50 ? 'text-yellow-700' : 'text-red-700'
                  }`}>
                    {healthScore.overall_grade}
                  </span>
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{healthScore.overall_score}%</p>
                  <p className="text-xs text-slate-500">Overall Health</p>
                </div>
              </div>

              {/* Dimension bars */}
              <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {(healthScore.dimensions || []).map(dim => (
                  <div key={dim.key} className="bg-slate-50 rounded-lg p-2.5" data-testid={`health-${dim.key}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs text-slate-500 truncate">{dim.label}</span>
                      <span className={`text-xs font-bold px-1 rounded ${
                        dim.grade === 'A' ? 'bg-emerald-100 text-emerald-700' :
                        dim.grade === 'B' ? 'bg-blue-100 text-blue-700' :
                        dim.grade === 'C' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                      }`}>{dim.grade}</span>
                    </div>
                    <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          dim.score >= 70 ? 'bg-emerald-500' : dim.score >= 50 ? 'bg-yellow-400' : 'bg-red-400'
                        }`}
                        style={{ width: `${dim.score}%` }}
                      />
                    </div>
                    <p className="text-xs font-semibold text-slate-700 mt-1">{dim.score}%</p>
                  </div>
                ))}
              </div>

              {/* Quick action */}
              <button
                onClick={() => navigate('/school/early-warnings')}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs font-semibold text-amber-700 hover:bg-amber-100 transition flex-shrink-0"
                data-testid="view-warnings-btn"
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                View Warnings
              </button>
            </div>
          ) : null}
        </div>
      )}

      {/* ── Summary Panels ──────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Attendance Summary */}
        <div className="edu-card p-6">
          <h3 className="font-manrope text-base font-semibold text-slate-900 mb-5">Attendance Summary</h3>
          {loading ? (
            <div className="space-y-4">{[...Array(2)].map((_, i) => <div key={i} className="h-6 bg-slate-100 rounded animate-pulse" />)}</div>
          ) : (
            <>
              <ProgressRow label="Present" value={presentToday} total={totalStudents} color="#16A34A" />
              <ProgressRow label="Absent" value={absentToday} total={totalStudents} color="#DC2626" />
              <ProgressRow label="Not Marked" value={Math.max(0, totalStudents - presentToday - absentToday)} total={totalStudents} color="#94A3B8" />
            </>
          )}
        </div>

        {/* Fee Summary */}
        <div className="edu-card p-6">
          <h3 className="font-manrope text-base font-semibold text-slate-900 mb-5">Fee Summary</h3>
          {loading ? (
            <div className="space-y-4">{[...Array(2)].map((_, i) => <div key={i} className="h-6 bg-slate-100 rounded animate-pulse" />)}</div>
          ) : feeTotal > 0 ? (
            <>
              <ProgressRow label="Collected" value={feePaid} total={feeTotal} color="#16A34A" />
              <ProgressRow label="Outstanding" value={feeBalance} total={feeTotal} color="#EF4444" />
            </>
          ) : (
            <p className="text-sm text-slate-400 text-center py-4">No fee records yet</p>
          )}
        </div>
      </div>

      {/* ── Charts ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Student Strength Bar Chart */}
        <div className="edu-card p-6">
          <h3 className="font-manrope text-base font-semibold text-slate-900 mb-5">Student Strength</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={classData} margin={{ top: 0, right: 0, bottom: 20, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} angle={-35} textAnchor="end" />
              <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 12, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07)' }} cursor={{ fill: '#F8FAFC' }} />
              <Bar dataKey="count" name="Students" fill="#0D9488" radius={[4, 4, 0, 0]} maxBarSize={40} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Attendance Trend */}
        <div className="edu-card p-6">
          <h3 className="font-manrope text-base font-semibold text-slate-900 mb-5">Attendance Trend (7 Days)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={trend} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 12, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07)' }} cursor={{ fill: '#F8FAFC' }} />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              <Bar dataKey="present" name="Present" fill="#4F46E5" radius={[3, 3, 0, 0]} maxBarSize={24} />
              <Bar dataKey="absent" name="Absent" fill="#FDA4AF" radius={[3, 3, 0, 0]} maxBarSize={24} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Recent Students + Activity ─────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent Students */}
        <div className="lg:col-span-2 edu-card overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="font-manrope text-sm font-semibold text-slate-900">Recent Students</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="edu-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Admission No.</th>
                  <th>Class</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}><td colSpan={4}><div className="h-4 bg-slate-100 rounded animate-pulse" /></td></tr>
                  ))
                ) : recentStudents.slice(0, 6).map((st, i) => (
                  <tr key={st.id || i}>
                    <td>
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-semibold text-[10px]">
                          {`${st.first_name?.[0] || ''}${st.last_name?.[0] || ''}`}
                        </div>
                        <span className="font-medium text-slate-800">{st.first_name} {st.last_name}</span>
                      </div>
                    </td>
                    <td className="text-slate-500">{st.admission_number}</td>
                    <td className="text-slate-500">{st.class_name}</td>
                    <td>
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${st.status === 'active' ? 'badge-success' : 'badge-neutral'}`}>
                        {st.status || 'Active'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="edu-card overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h3 className="font-manrope text-sm font-semibold text-slate-900">Recent Activity</h3>
          </div>
          <div className="p-4 space-y-3">
            {(data?.recent_activities || []).slice(0, 6).map((act, i) => (
              <div key={i} className="flex gap-3 items-start">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-2 flex-shrink-0" />
                <div>
                  <p className="text-xs text-slate-700">{act.description}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{act.created_at?.slice(0, 10)}</p>
                </div>
              </div>
            ))}
            {(!data?.recent_activities || data.recent_activities.length === 0) && (
              <p className="text-xs text-slate-400 text-center py-6">No recent activity</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
